import { LabelValue } from '~/components/component-library/LabelValue'
import {
  TransactionTimelineRow,
  TransactionTimelineRowProps,
} from '~/components/txn-display/timeline/components/TimelineRow'
import { useBridgeRow } from '~/components/txn-display/timeline/hooks/useBridgeRow'
import { useCreatedByRow } from '~/components/txn-display/timeline/hooks/useCreatedByRow'
import { useEoaSignerRows } from '~/components/txn-display/timeline/hooks/useEoaSignerRows'
import { usePasskeySignerRows } from '~/components/txn-display/timeline/hooks/usePasskeySignerRows'
import { useRemainingSignatures } from '~/components/txn-display/timeline/hooks/useRemainingSignatures'
import { useStatusRow } from '~/components/txn-display/timeline/hooks/useStatusRow'
import { buildSecondaryDisplay } from '~/components/txn-display/timeline/utils/buildSecondaryDisplay'
import { getSmartAccountRole } from '~/components/txn-display/timeline/utils/getSmartAccountRole'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { BackendTransaction } from '~/types'
import { cn } from '~/utils/tailwind-utils'

export const TransactionTimeline = ({
  transaction,
}: {
  transaction: BackendTransaction
}) => {
  const { smartAccount } = useSmartAccount(transaction.smartAccountAddress)
  const smartAccountRole = getSmartAccountRole(smartAccount, transaction)

  const passkeySignerRows = usePasskeySignerRows({ transaction })
  const eoaSignerRows = useEoaSignerRows({ transaction, smartAccountRole })
  const createdByRow = useCreatedByRow({ transaction, smartAccountRole })
  const statusRow = useStatusRow({ transaction })
  const { remaining } = useRemainingSignatures({ transaction })
  const bridgeRow = useBridgeRow({ transaction })

  // Passkey and EOA signatures arrive in any order; sort the combined set by
  // signedAt so the timeline reads chronologically. Unsigned rows sort last.
  const signerRows = [...passkeySignerRows, ...eoaSignerRows].sort(
    (a, b) =>
      (a.signedAt ? new Date(a.signedAt).getTime() : Number.MAX_SAFE_INTEGER) -
      (b.signedAt ? new Date(b.signedAt).getTime() : Number.MAX_SAFE_INTEGER),
  )

  const rows = [createdByRow, ...signerRows, statusRow, ...bridgeRow].filter(
    Boolean,
  )

  return (
    <LabelValue
      label="Status"
      value={buildSecondaryDisplay(transaction, remaining)}
      isCollapsible
      size="small"
      defaultOpen={false}
    >
      <div
        className={cn(
          'relative flex flex-col rounded-lg bg-white px-3.5 py-2.5 ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-700',
        )}
      >
        {rows.map((row, index) => (
          <TransactionTimelineRow
            key={row?.key}
            row={row as TransactionTimelineRowProps}
            index={index}
          />
        ))}
      </div>
    </LabelValue>
  )
}
